<template>
    <div class="container">
        <v-title :breadcrumb="breadcrumb">
            Upcoming Invoice
        </v-title>
        <div class="grid">
            <div class="grid-item grid-item-primary">
                <div class="grid">
                    <div class="grid-item grid-item-full" v-for="invoice in upcomingInvoices">
                        <div class="tile position-relative">
                            <v-inline-loader v-if="isLoading"/>
                            <div class="tile-content" v-else>
                                <invoice-context :invoice="invoice" />
                                <invoice-items :invoice="invoice" ref="invoiceItems">
                                    <template slot="amountDue">
                                        <tr>
                                            <td><strong>Amount Due</strong></td>
                                            <td class="text-right is-numeric">{{invoice.amountDue | monetize(invoice.currency)}}</td>
                                        </tr>
                                    </template>
                                </invoice-items>
                            </div>
                            <div class="tile-content" v-if="invoice.isUpcoming">
                                <div class="grid-content align-items-center">
                                    <div class="grid-item text-right">
                                        <r-button :loading="isProcessingPayment" @click="startPayment">Early Pay</r-button>
                                    </div>
                                </div>
                                <invoice-early-pay
                                    v-if="isPaying"
                                    :invoice="invoice"
                                    @close="stopPayment"
                                    @issue-early-pay="issueEarlyPay"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="grid-item grid-item-full">
                        <timeline
                            ref="timeline"
                            type="order"
                            :key="`order-${order.id}-timeline`"
                            :entity="order"/>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-secondary">
                <div class="grid">
                    <div class="grid-item grid-item-full" v-if="$auth.subscriptions.read.isAllowed">
                        <order-details-tile
                            :order="order"
                            :summary="summary"
                            :customer="customer"
                            :is-loading="isLoading"
                            @refresh="fetchData({updateTimeline: true})"
                        />
                    </div>
                    <div class="grid-item grid-item-full" v-if="$auth.invoices.read.isAllowed">
                        <div class="tile position-relative">
                            <div class="tile-title">
                                <slot name="title">
                                    <h2>Invoices for this Order</h2>
                                </slot>
                            </div>
                            <invoices
                                :invoices-params="{
                                    sort: `-issuedTime`,
                                    filter: `subscriptionId:${id}`,
                                    expand: 'organization,items.*.product,items.*.plan',
                                }">
                                <template slot="headers">
                                    <th>Invoice<br/>Number</th>
                                    <th>Date<br/>Issued</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                </template>
                                <template slot="iteratee" slot-scope="{item}">
                                    <td v-fs-block>
                                        <router-link :to="{name: 'invoice-details', params: {id: item.id}}">
                                            {{item.invoiceNumberLabel}}
                                        </router-link>
                                    </td>
                                    <td class="no-wrap" v-fs-block>{{item.issuedTime | dateUTC('shortDate')}}</td>
                                    <td class="no-wrap" v-fs-block>{{item.amount | monetize(item.currency)}}</td>
                                    <td v-fs-block>
                                        <r-badge :type="getBadgeType('invoiceStatus', item.status)">
                                            {{item.statusLabel}}
                                        </r-badge>
                                    </td>
                                </template>
                                <template slot="no-content">
                                    <p class="text-muted">This order has no other invoices</p>
                                </template>
                            </invoices>
                        </div>
                    </div>
                    <div class="grid-item grid-item-full">
                        <risk-analysis :model="order"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import vContact from '@/components/v-contact';
    import vInlineLoader from '@/components/v-inline-loader';
    import vTitle from '@/components/v-title';
    import {getBadgeType} from '@/data/badges-color-map';
    import {vIcon} from '@/components/form';

    import LoadStateMixin from '@/mixins/load-state-mixin';

    import CustomerModel from '@/common/models/customer-model';
    import InvoiceModel from '@/common/models/invoice-model';
    import OrderModel from '@/common/models/order-model';
    import OrderSummaryModel from '@/common/models/order-summary-model';

    import InvoiceContext from '@/modules/invoices/invoice-context';
    import InvoiceItems from '@/modules/invoices/invoice-items';
    import Invoices from '@/modules/invoices/invoices-list';
    import Timeline from '@/components/timeline/timeline';
    import RiskAnalysis from '@/components/risk-analysis/risk-analysis';
    import OrderDetailsTile from '@/modules/invoices/components/order-details-tile';

    export default {
        name: 'upcoming-invoice-details',
        components: {
            vContact,
            vIcon,
            vInlineLoader,
            vTitle,
            Timeline,
            RiskAnalysis,
            InvoiceContext,
            InvoiceItems,
            Invoices,
            OrderDetailsTile,
            InvoiceEarlyPay: () => import('@/modules/invoices/components/invoice-early-pay'),
        },
        mixins: [LoadStateMixin],
        data() {
            return {
                id: this.$route.params.id,
                isLoading: false,
                customer: new CustomerModel(),
                order: new OrderModel(),
                summary: null,
                upcomingInvoices: [],
                isProcessingPayment: false,
                isPaying: false,
            };
        },
        computed: {
            breadcrumb() {
                return {
                    label: 'Upcoming Invoice',
                    route: {
                        name: 'order-upcoming-invoice',
                        params: {
                            id: this.id,
                        },
                    },
                };
            },
        },
        methods: {
            getBadgeType,
            async fetchData({updateTimeline = false} = {}) {
                this.isLoading = true;
                try {
                    const subscriptionId = this.id;
                    const upcomingInvoicesParams = {
                        id: subscriptionId,
                        expand: 'organization,items.*.product,items.*.plan',
                    };
                    const subscriptionParams = {
                        id: subscriptionId,
                        expand: 'customer',
                    };
                    const [
                        {items: invoicesItems},
                        {fields: subscriptionFields},
                        {fields: summaryFields},
                    ] = await Promise.all([
                        this.$api(({subscriptions}) => subscriptions.getAllUpcomingInvoices(upcomingInvoicesParams)),
                        this.$api(({subscriptions}) => subscriptions.get(subscriptionParams)),
                        (async () => {
                            try {
                                return await this.$api(({subscriptions}) => subscriptions.getSubscriptionSummaryMetrics({subscriptionId}));
                            } catch (error) {
                                this.summaryOrderInterval();
                                return {fields: null};
                            }
                        })(),
                    ]);

                    this.upcomingInvoices = invoicesItems.map(({fields}) => new InvoiceModel(fields));
                    this.order = new OrderModel(subscriptionFields);

                    if (summaryFields !== null) {
                        this.summary = new OrderSummaryModel(summaryFields);
                    }

                    if (updateTimeline) {
                        this.$refs.timeline.update();
                    }
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isLoading = false;
                }
            },
            summaryOrderInterval() {
                let counter = 0;
                const subscriptionId = this.id;
                const run = () => {
                    window.setTimeout(() => {
                        (async () => {
                            try {
                                const {fields} = await this.$api(({subscriptions}) => subscriptions.getSubscriptionSummaryMetrics({subscriptionId}));
                                this.summary = new OrderSummaryModel(fields);
                            } catch (e) {
                                counter += 1;
                                if (counter > 5) {
                                    this.summary = {};
                                    return;
                                }
                                run();
                            }
                        })();
                    }, 1000);
                };
                run();
            },
            startPayment() {
                this.isPaying = true;
            },
            stopPayment() {
                this.isPaying = false;
            },
            async issueEarlyPay({invoiceId, data}) {
                this.isProcessingPayment = true;
                try {
                    const params = {
                        id: this.id,
                        invoiceId,
                        data,
                    };
                    await this.$api(({subscriptions}) => subscriptions.issueUpcomingInvoice(params));
                    this.$toast.positive('Successfully issued the invoice for early pay.');
                    this.$router.push({name: 'invoice-details', params: {id: invoiceId}});
                } catch (err) {
                    this.$toast.fromError(err, `Unable to issue the invoice at this time.`);
                } finally {
                    this.isProcessingPayment = false;
                }
            },
        },
    };
</script>
